/** The keyboard map, on `?`.
 *
 * The cheapest discoverability feature there is, and the one people reach for
 * first when they are lost. It exists so a shortcut printed on a control is not
 * the ONLY way to learn it — and so a keyboard-only user has somewhere to go
 * that is not the mouse.
 */
import * as React from "react";

import { Dialog, DialogContent } from "@/components/ui/overlays";
import { useShell } from "@/stores/shell";

/** One row of the map. `keys` are rendered as separate caps. */
type Shortcut = { keys: string[]; label: string };

const GROUPS: { title: string; items: Shortcut[] }[] = [
  {
    title: "Panels",
    items: [
      { keys: ["["], label: "Show or hide the library (left)" },
      { keys: ["]"], label: "Show or hide the context panel (right)" },
      { keys: ["\\"], label: "Show or hide the top bar" },
    ],
  },
  {
    title: "Getting around",
    items: [
      { keys: ["Space"], label: "Open Tools — every action in one place" },
      { keys: ["?"], label: "This map" },
      { keys: ["Esc"], label: "Close whatever is open" },
    ],
  },
  {
    title: "Writing",
    items: [
      { keys: ["1"], label: "Pen" },
      { keys: ["2"], label: "Highlighter" },
      { keys: ["3"], label: "Eraser" },
      { keys: ["Ctrl", "Z"], label: "Undo the last mark" },
      { keys: ["Ctrl", "Y"], label: "Redo" },
    ],
  },
];

function Cap({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[22px] items-center justify-center rounded border border-border border-b-2 bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-none text-foreground">
      {children}
    </kbd>
  );
}

export function ShortcutSheet() {
  const open = useShell((s) => s.shortcutsOpen);
  return (
    <Dialog
      open={open}
      onOpenChange={(v) =>
        v ? useShell.getState().openShortcuts() : useShell.getState().closeShortcuts()
      }
    >
      <DialogContent title="Keyboard" className="w-[min(560px,92vw)]">
        <div className="flex flex-col gap-5">
          {GROUPS.map((g) => (
            <section key={g.title}>
              <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[0.13em] text-muted-foreground">
                {g.title}
              </h3>
              <ul className="flex flex-col gap-1.5">
                {g.items.map((s) => (
                  <li key={s.label} className="flex items-center gap-3 text-[13px]">
                    <span className="flex shrink-0 items-center gap-1">
                      {s.keys.map((k) => (
                        <Cap key={k}>{k}</Cap>
                      ))}
                    </span>
                    <span className="text-muted-foreground">{s.label}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="mt-5 border-t border-border pt-3 text-[12px] text-muted-foreground">
          Every shortcut here is also printed on the control it drives, so you pick them up by
          using the mouse.
        </p>
      </DialogContent>
    </Dialog>
  );
}
